import { useEffect, useState } from "react";
import { useDashboardV2Store } from "@/store/api/dashboardV2Store";
import { useDashboardConfigStore } from "@/store/dashboardConfigStore";
import { DashboardFilters } from "@/components/dashboard/v2/DashboardFilters";
import { WidgetRenderer } from "@/components/dashboard/WidgetRenderer";
import { useTranslation } from "react-i18next";
import { LayoutGrid } from "lucide-react";

export function OverviewTab() {
  const { t } = useTranslation(["dashboard", "adminui2"]);
  const [chartsLoaded, setChartsLoaded] = useState(false);
  const [insightsLoaded, setInsightsLoaded] = useState(false);
  
  const { getVisibleWidgets } = useDashboardConfigStore();
  
  const {
    kpisLoading,
    growthData,
    growthLoading,
    memberEvolutionData,
    memberEvolutionLoading,
    heatmapData,
    heatmapLoading,
    filters,
    fetchAllKPIs,
    fetchGrowthData,
    fetchMemberEvolution,
    fetchHeatmap,
    fetchInsights,
    setFilters,
    resetFilters,
  } = useDashboardV2Store();

  // Phase 1: Load critical KPIs immediately
  useEffect(() => {
    fetchAllKPIs(filters.period);
  }, [filters.period, fetchAllKPIs]);

  // Phase 2: Load charts after a short delay (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchGrowthData();
      fetchMemberEvolution(filters.period);
      fetchHeatmap(filters.period);
      setChartsLoaded(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.period, fetchGrowthData, fetchMemberEvolution, fetchHeatmap]);

  // Phase 3: Load insights after charts (600ms total)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInsights(filters.period);
      setInsightsLoaded(true);
    }, 600);

    return () => clearTimeout(timer);
  }, [filters.period, fetchInsights]);

  const handlePeriodChange = (period: 'today' | 'week' | 'month' | 'year') => {
    setFilters({ period });
  };

  const visibleWidgets = getVisibleWidgets();

  // Empty state when no widgets are visible
  if (visibleWidgets.length === 0) {
    return (
      <div className="space-y-6">
        <DashboardFilters
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onReset={resetFilters}
        />
        
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <LayoutGrid className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">{t("overviewTab.emptyTitle", { ns: "adminui2" })}</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {t("overviewTab.emptyDesc", { ns: "adminui2" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <DashboardFilters
        period={filters.period}
        onPeriodChange={handlePeriodChange}
        onReset={resetFilters}
      />

      {/* Dynamic Widget Rendering */}
      <WidgetRenderer
        period={filters.period}
        kpisLoading={kpisLoading}
        growthData={growthData}
        growthLoading={growthLoading}
        memberEvolutionData={memberEvolutionData}
        memberEvolutionLoading={memberEvolutionLoading}
        heatmapData={heatmapData}
        heatmapLoading={heatmapLoading}
      />
    </div>
  );
}
